import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import {
  PrismBanner,
  PrismButton,
  PrismIcon,
  PrismModal,
} from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { eq } from 'ember-truth-helpers';
import type { FileWithValidation } from 'lumen/components/general/file-drop-zone';
import GeneralFileDropZone from 'lumen/components/general/file-drop-zone';
import type { FileType } from 'lumen/utils/constants/file-types';

type Step = 'drop-zone' | 'file-ready';
type ValidationError = FileWithValidation['validationErrors'][number];

interface SettingsCentralMappingModalsModalUploadMappingSignature {
  Args: {
    isUploadRunning?: boolean;
    onClearUploadError?: () => void;
    onClose: () => void;
    onUpload: (file: File) => void;
    uploadErrorMessage?: string;
  };
}

export default class SettingsCentralMappingModalsModalUploadMapping extends Component<SettingsCentralMappingModalsModalUploadMappingSignature> {
  @service declare intl: Services['intl'];

  @tracked file: File | null = null;
  @tracked isTooManyFiles = false;
  @tracked isValid = false;
  @tracked step: Step = 'drop-zone';
  @tracked validationError: ValidationError | null = null;

  allowedFormats: FileType[] = ['xlsx'];

  get bannerMessage(): string | undefined {
    return this.args.uploadErrorMessage ?? this.validationErrorMessage;
  }

  get bannerTitle(): string | undefined {
    // Only server-side failures get the title, since client-side validation
    // happens before the user has actually tried to upload anything.
    return this.args.uploadErrorMessage
      ? this.intl.t(
          'settings.central_mapping.upload_mapping.error.banner_title',
        )
      : undefined;
  }

  get isFileConfirmedValid(): boolean {
    return this.isFileValid && !this.args.uploadErrorMessage;
  }

  get isFileValid(): boolean {
    return this.isValid && !this.isTooManyFiles;
  }

  get isUploadDisabled(): boolean {
    return (
      !this.isFileValid ||
      !this.file ||
      Boolean(this.args.uploadErrorMessage) ||
      Boolean(this.args.isUploadRunning)
    );
  }

  get validationErrorMessage(): string | undefined {
    if (this.isTooManyFiles) {
      return this.intl.t(
        'settings.central_mapping.upload_mapping.error.too_many_files',
      );
    }

    switch (this.validationError) {
      case 'FileTooLarge':
        return this.intl.t(
          'settings.central_mapping.upload_mapping.error.file_too_large',
        );
      case 'InvalidFileType':
        return this.intl.t(
          'settings.central_mapping.upload_mapping.error.invalid_file_type',
        );
      default:
        return undefined;
    }
  }

  @action
  closeModal(): void {
    if (this.args.isUploadRunning) {
      return;
    }

    this.args.onClose();
  }

  @action
  goBack(): void {
    this.step = 'drop-zone';
    this.file = null;
    this.isValid = false;
    this.isTooManyFiles = false;
    this.validationError = null;
    this.args.onClearUploadError?.();
  }

  @action
  handleUpload(): void {
    if (!this.file || !this.isFileValid || this.args.uploadErrorMessage) {
      return;
    }

    this.args.onUpload(this.file);
  }

  @action
  onFileDrop(
    filesWithValidation: FileWithValidation[],
    isTooManyFiles: boolean,
  ): void {
    const [droppedFile] = filesWithValidation;

    if (droppedFile) {
      this.file = droppedFile.file;
      this.validationError = droppedFile.validationErrors[0] ?? null;
      this.isValid = droppedFile.validationErrors.length === 0;
      this.isTooManyFiles = isTooManyFiles;

      this.step = 'file-ready';
      this.args.onClearUploadError?.();
    }
  }

  <template>
    <PrismModal
      @isCloseDisabled={{@isUploadRunning}}
      @onClose={{this.closeModal}}
      @title={{t "settings.central_mapping.upload_mapping"}}
      data-test-id="upload-mapping-modal"
    >
      <:default>
        <div class="modal--padded modal--small flex flex-col gap-500">
          {{#if this.bannerMessage}}
            <PrismBanner
              @bannerMessage={{this.bannerMessage}}
              @bannerStyle="critical"
              @bannerTitle={{this.bannerTitle}}
              @icon="warning"
              data-test-id="upload-mapping-error-banner"
              role="alert"
            />
          {{/if}}

          {{#if (eq this.step "drop-zone")}}
            <GeneralFileDropZone
              @allowedFormats={{this.allowedFormats}}
              @onFileDrop={{this.onFileDrop}}
              class="min-h-[240px]"
            />
          {{/if}}

          {{#if (eq this.step "file-ready")}}
            <div class="bg-neutral-subdued flex gap-200 px-400 py-500">
              <PrismIcon @name="file" />
              <div
                class="heading-4 mr-auto truncate"
                data-test-id="upload-mapping-file-name"
                title={{if this.file this.file.name ""}}
              >
                {{#if this.file}}
                  {{this.file.name}}
                {{/if}}
              </div>
              {{#if this.isFileConfirmedValid}}
                <PrismIcon
                  @color="success"
                  @name="circle-checkmark"
                  data-test-id="upload-mapping-file-valid"
                />
              {{else}}
                <PrismIcon
                  @color="critical"
                  @name="circle-cross"
                  data-test-id="upload-mapping-file-invalid"
                />
              {{/if}}
            </div>
          {{/if}}
        </div>
      </:default>
      <:footer>
        <div class="mx-500 flex h-full items-center justify-end gap-200">
          {{#if (eq this.step "file-ready")}}
            <PrismButton
              @buttonType="ghost"
              @iconBefore="arrow-left"
              @isDisabled={{@isUploadRunning}}
              @label={{t "general.back"}}
              @onClick={{this.goBack}}
              class="mr-auto"
              data-test-id="upload-mapping-back"
            />
          {{/if}}
          <PrismButton
            @buttonType="secondary"
            @isDisabled={{@isUploadRunning}}
            @label={{t "general.cancel"}}
            @onClick={{this.closeModal}}
            data-test-id="upload-mapping-cancel"
          />
          <PrismButton
            @isDisabled={{this.isUploadDisabled}}
            @isLoading={{@isUploadRunning}}
            @label={{t "general.upload"}}
            @onClick={{this.handleUpload}}
            data-test-id="upload-mapping-upload"
          />
        </div>
      </:footer>
    </PrismModal>
  </template>
}
